import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Platform, View, SafeAreaView, Text, ActivityIndicator, TouchableOpacity, PermissionsAndroid, TextInput, Image, StyleSheet, KeyboardAvoidingView, ScrollView, Alert } from 'react-native';
import { Audio } from 'expo-av';
import { NavigationProp } from '@react-navigation/native';
import Voice from '@react-native-voice/voice';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as FileSystem from 'expo-file-system';
import { RouteProp, useIsFocused } from '@react-navigation/native';
import { RootStackParamList } from './App.web';
import { apiUrl } from './utils/config';
import { useStory } from './StoryContext';
import * as Font from 'expo-font';
import { GestureHandlerRootView } from "react-native-gesture-handler";
import backBtn from './assets/back-btn.png';
import muteBtn from './assets/mute-btn.png';
import unmuteBtn from './assets/unmute-btn.png';
import flagBtn from './assets/flag-icon.png';
import FeedbackModal from './FeedbackModal';

type StoryPageRouteProp = RouteProp<RootStackParamList, 'StoryPage'>;

const isMobileWeb = Platform.OS === 'web' && /Mobi|Android/i.test(navigator.userAgent);

const StoryPage: React.FC<{
  route: StoryPageRouteProp;
  navigation: NavigationProp<RootStackParamList, 'CharacterDetailsPage'>;
}> = ({ route, navigation }) => {
  const { context, setContext } = useStory();
  if (!context) {
    return <Text>Error: No story context available</Text>;
  }

  const { sessionId, storyData, characterDescription, initialResponse, withImages } = context;
  const loadFonts = async () => {
        await Font.loadAsync({
          'Futura': require('./assets/fonts/futura.ttf'),
        });
      };
    
  useEffect(() => {
    loadFonts();
  });
  const [textInput, setTextInput] = useState('');
  const [voice, setVoice] = useState('Matthew');
  const [volume, setVolume] = useState(50);
  const [speed, setSpeed] = useState(1);
  const [promptNumber, setPromptNumber] = useState(() => storyData.prompt_number + 1);
  const [chapter, setChapter] = useState(() => Math.max(1, Math.ceil((storyData.prompt_number + 1) / 7)));

  const [imageUrl, setImageUrl] = useState(() => {
    if (storyData.prompt_number === 0 || storyData.prompt_number === undefined) {
      return initialResponse?.first_image || storyData.first_image || null;
    }
    return initialResponse?.image_url || storyData.image_url || null;
  });
  const imagesEnabled = withImages;
  const [loading, setLoading] = useState(true);
  const [responseType, setResponseType] = useState('questions');
  const [storyContent, setStoryContent] = useState(() => {
    return typeof initialResponse === 'string'
      ? initialResponse
      : initialResponse?.response || 'No story content available.';
  });  
  const [audioBase64, setAudioBase64] = useState(initialResponse?.audio || null);
  const isFocused = useIsFocused();
  const [isMuted, setIsMuted] = useState(false);
  const [activeRecognizer, setActiveRecognizer] = useState(false);

  const [webAudioInstance, setWebAudioInstance] = useState<HTMLAudioElement | null>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const isNextDisabled =
    responseType !== 'cliffhanger' &&
    responseType !== 'final_prompt' &&
    textInput.trim().length === 0;
  
  const audioRef = useRef<Audio.Sound | null>(null);
  const [pages, setPages] = useState<StoryPageData[]>([]);

  const [imageJobs, setImageJobs] = useState<{ current: string | null, next: string | null }>({
    current: null,
    next: null,
  });
  const [nextImageUrl, setNextImageUrl] = useState<string | null>(null);


  const activePollingJobRef = useRef<string | null>(null);
  const [flagModalVisible, setFlagModalVisible] = useState(false);
  const [flagType, setFlagType] = useState<'image' | 'text' | 'both' | null>(null);
  const [flagReason, setFlagReason] = useState('');
  const [hideImage, setHideImage] = useState(false);
  const [hideText, setHideText] = useState(false);
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [feedbackShown, setFeedbackShown] = useState(false);



  type StoryPageData = {
    prompt_number: number;
    audio_s3_key: string;
    text_response: string;
    job_id: string | null;
    audio: string | null;
  };
  

  useEffect(() => {
    const initializePage = async () => {
      setLoading(true);
  
      try {
        // 1) Fetch user settings
        const { voice, volume, speed } = await fetchUserSettings();
        setVoice(voice);
        setVolume(volume);
        setSpeed(speed);

        if (volume === 0) {
          setIsMuted(true);
        }
  
        // 2) Fetch the last message for this story
        const authToken = await getAuthToken(); // or however you do it
        if (!authToken) throw new Error('Missing auth token');
  
        const response = await fetch(
          `${apiUrl}story/get-last-message?session_id=${sessionId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch last message. Status: ${response.status}`);
        }
  
        const data = await response.json();
        // Convert audio if present
        const latestAudioBase64 = data.latest_audio
          ? await convertAudioUrlToBase64(data.latest_audio)
          : null;
        setAudioBase64(latestAudioBase64)
        setStoryContent(data.latest_message?.content);
        await setPromptNumber(data.prompt_number + 1);
        setChapter(getCurrentChapter(data.prompt_number + 1));
        // Always show the current image/job (not the prefetch/next)
        setImageUrl(data.image_url || data.first_image || null);
        setImageJobs({
          current: data.job_id || null, // job_id is for current image (Prompt N)
          next: data.next_job_id || null, // next_job_id is for future image (Prompt N+1)
        });
        setNextImageUrl(null);
        if (data.next_job_id) {
          pollForNextImage(data.next_job_id);
        }

        setResponseType(determineResponseType(data.prompt_number, storyData.chapters));
        
        // 4) If we have new audio, save & play it
        if (audioBase64) {
          await saveBase64AudioToFile(audioBase64);
        }
      
      if (data.pages) {
        setPages(data.pages);
      }
  
      } catch (error) {
        console.error('Error during initialization:', error);
      } finally {
        setLoading(false);
      }
    };
  
    initializePage();
  
    return () => {
      if (audioRef.current) {
        audioRef.current.unloadAsync(); // Cleanup audio
      }
    };
  }, []);

  const fetchPageAssets = async (audioKey: string, jobId: string) => {
    try {
      const authToken = await getAuthToken();
      const response = await fetch(
        `${apiUrl}story/get-page?audio_key=${audioKey}&job_id=${jobId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch assets');
  
      const data = await response.json();
      setAudioBase64(data.audio);
      setImageUrl(data.image_url);
      
      stopAudio();
      if (data.audio) {
        saveBase64AudioToFile(data.audio);
      }
    } catch (err) {
      console.error('Error fetching page assets:', err);
    }
  };
  
  const handlePrevious = () => {
    const maxPromptInPages = Math.max(...pages.map(p => p.prompt_number));
    const offset = promptNumber > maxPromptInPages ? 2 : 1;
    const previousPage = pages.find(page => page.prompt_number === promptNumber - offset);
  
    if (previousPage) {
      setPromptNumber(previousPage.prompt_number);
      setStoryContent(previousPage.text_response);
      const audioKey = previousPage.audio_s3_key || '';
      fetchPageAssets(audioKey, previousPage.job_id || '');
    }
  };
  
  const handleNext = () => {
    const nextPage = pages.find((page) => page.prompt_number === promptNumber + 1);
  
    if (nextPage) {
      const maxPrompt = Math.max(...pages.map((p) => p.prompt_number));
      const isMostRecent = nextPage.prompt_number === maxPrompt;
  
      setPromptNumber(isMostRecent ? nextPage.prompt_number + 1 : nextPage.prompt_number);
      setStoryContent(nextPage.text_response);
      const audioKey = nextPage.audio_s3_key || '';
      fetchPageAssets(audioKey, nextPage.job_id || '');
    } else {
      handleTextInputSubmit();
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    }
  };
  
  
  // Determine if the Back button should appear
  const showBackButton = useMemo(() => {
    const maxPrompt = Math.max(...pages.map(p => p.prompt_number), 0);
    if (maxPrompt <= 1) return false;
    return pages.some((page) => page.prompt_number === promptNumber - 1);
  }, [pages, promptNumber]);

  const showNextPageButton = useMemo(() => {
    return pages.some((page) => page.prompt_number === promptNumber + 1);
  }, [pages, promptNumber]);
  

  const saveBase64AudioToFile = async (base64Audio: string) => {
    try {
      if (Platform.OS === 'web') {
        playAudioWeb(base64Audio);
      } else {
        // Mobile: Save to filesystem
        const uri = `${FileSystem.cacheDirectory}story-audio.wav`;
        await FileSystem.writeAsStringAsync(uri, base64Audio, {
          encoding: FileSystem.EncodingType.Base64,
        });
        playAudio(uri); // Play audio after saving
      }
    } catch (error) {
      console.error('Error saving audio file:', error);
      Alert.alert('Error', 'Failed to save audio file.');
    }
  };

  const convertAudioUrlToBase64 = async (audioUrl: string): Promise<string | null> => {
      try {
        if (Platform.OS === 'web') {
  
          const response = await fetch(audioUrl, {
            method: 'GET',
            mode: 'cors', // Ensure CORS is enabled
            headers: {
              'Accept': 'audio/wav', // Adjust if using MP3
            },
          });
  
          if (!response.ok) {
            console.error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
            return null;
          }
  
          const arrayBuffer = await response.arrayBuffer();
          const base64String = btoa(
            new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
  
          return base64String;
        } else {
          // Mobile: Use expo-file-system
          const downloadResumable = FileSystem.createDownloadResumable(
            audioUrl,
            FileSystem.cacheDirectory + 'audio.wav'
          );
  
          const result = await downloadResumable.downloadAsync();
  
          if (result?.uri) {
            const base64Audio = await FileSystem.readAsStringAsync(result.uri, {
              encoding: FileSystem.EncodingType.Base64,
            });
            return base64Audio;
          } else {
            console.error('Failed to download the audio file');
            return null;
          }
        }
      } catch (error) {
        console.error('Error converting audio to Base64:', error);
        return null;
      }
    };
  
  const setAudioMode = async () => {
    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        staysActiveInBackground: true,
        playsInSilentModeIOS: true, // Allow audio playback even when the device is on silent
      });
    } catch (error) {
      console.error('Error setting audio mode:', error);
    }
  };

  const playAudio = async (uri: string) => {
    try {
      if (Platform.OS === 'ios'){
        setAudioMode();
      }
      let settingsVolume = 0; // Default to 0 for muted
      let speed = 1; // Default playback speed
      const settings = await fetchUserSettings();
      speed = settings.speed;
  
      if (!isMuted) {
        settingsVolume = settings.volume / 100; // Convert to 0-1 range
      }
  
      const { sound } = await Audio.Sound.createAsync({ uri });
      audioRef.current = sound;
      // Apply volume and playback speed
      await sound.setVolumeAsync(isMuted ? 0 : settingsVolume);
      await sound.setRateAsync(speed, true);
  
      await sound.playAsync();
      sound.setOnPlaybackStatusUpdate((status) => {
        if (status.isLoaded && status.didJustFinish) {
          console.log('Audio finished playing.');
        }
      });
    } catch (error) {
      console.error('Error playing audio:', error);
      Alert.alert('Error', 'Failed to play audio.');
    }
  };

  const playAudioWeb = async (base64Audio: string) => {
    try {
      console.log('playing audio')
      const settings = await fetchUserSettings();
  
      let settingsVolume = isMuted ? 0 : settings.volume / 100;
      let speed = settings.speed || 1;
  
      const newAudio = new window.Audio(`data:audio/wav;base64,${base64Audio}`);
  
      newAudio.volume = settingsVolume;
      newAudio.playbackRate = speed;
      newAudio.onended = () => {
        console.log('ending');
        setWebAudioInstance(null); // Reset instance when done
      };
  
      newAudio.play();
      setWebAudioInstance(newAudio); // Store the playing audio in state
  
    } catch (error) {
      console.error('Error playing audio on Web:', error);
      Alert.alert('Error', 'Failed to play audio on Web.');
    }
  };
   

  const stopAudio = async () => {
    if (Platform.OS === 'web') {
      if (webAudioInstance) {
        webAudioInstance.pause();
        webAudioInstance.currentTime = 0;
        setWebAudioInstance(null);
      }
    } else {
      if (audioRef.current) {
        try {
          const status = await audioRef.current.getStatusAsync();
          if (status.isLoaded) {
            await audioRef.current.stopAsync();
            await audioRef.current.unloadAsync();
          }
        } catch (error) {
          console.error('Error stopping or unloading mobile audio:', error);
        } finally {
          audioRef.current = null;
        }
      }
    }
  };
  

  useEffect(() => {
    if (!isFocused) {
      stopAudio(); // Stop audio when the page is not focused
    }
  }, [isFocused]);

  useEffect(() => {
    const currentJobId = imageJobs.current;
    if (!imagesEnabled || !currentJobId) return;

    const interval = setInterval(async () => {
      try {
        const authToken = await getAuthToken();
        if (!authToken) throw new Error('Missing auth token');

        const response = await fetch(`${apiUrl}story/check-image-status?job_id=${currentJobId}&session_id=${sessionId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        });

        const data = await response.json();
        if (data.status === 'COMPLETED' && data.image_url) {
          setImageUrl(data.image_url);
          setImageJobs((prev) => ({ ...prev, current: null }));
          clearInterval(interval);
        } else if (data.status === 'FAILED') {
          clearInterval(interval);
          Alert.alert('Error', 'Failed to generate image.');
        }
      } catch (err) {
        clearInterval(interval);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [imageJobs.current, imagesEnabled]);


  useEffect(() => {
  // No listeners at mount (move inside startListening)
  return () => {
    Voice.stop();
    Voice.destroy().then(Voice.removeAllListeners);
  };
}, []);


  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);
  
  // Fetch user settings on mount
  const fetchUserSettings = useCallback(async (): Promise<{ voice: string; volume: number; speed: number }> => {
    try {
      const authToken = await fetchAuthSession().then((session) => session?.tokens?.idToken || null);
      const userID = await fetchAuthSession().then((session) => session?.tokens?.accessToken?.payload?.sub || null);
  
      if (!authToken || !userID) {
        throw new Error('Missing auth token or user ID');
      }
  
      const response = await fetch(`${apiUrl}settings/get-user-settings?user_id=${userID}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch user settings');
      }
  
      const settings = await response.json();
      
      setVoice(settings.voice);
      setVolume(settings.volume);
      setSpeed(settings.speed);
      // Return the fetched settings
      return {
        voice: settings.voice || 'Matthew',
        volume: settings.volume,
        speed: settings.speed || 1,
      };
    } catch (error) {
      console.error('Error fetching user settings:', error);
      Alert.alert('Error', 'Failed to fetch user settings. Using defaults.');
  
      // Return default settings in case of error
      return {
        voice: 'Matthew',
        volume: 50,
        speed: 1,
      };
    }
  }, []);
  
  
  useEffect(() => {
    fetchUserSettings();
  }, []);

  const pollForNextImage = useCallback((jobId: string) => {
    if (!jobId || activePollingJobRef.current === jobId) return;
    activePollingJobRef.current = jobId;

    const interval = setInterval(async () => {
      try {
        const authToken = await getAuthToken();
        if (!authToken) throw new Error('Missing auth token');

        const response = await fetch(
          `${apiUrl}story/check-image-status?job_id=${jobId}&session_id=${sessionId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        const data = await response.json();

        if (data.status === 'COMPLETED' && data.image_url) {
          setImageJobs(prev => {
            // Match to correct bucket
            if (prev.current === jobId) {
              setImageUrl(data.image_url);
              return { ...prev, current: null };
            } else if (prev.next === jobId) {
              setNextImageUrl(data.image_url);
              Image.prefetch(data.image_url).then(() => {
                console.log('Image prefetched:', data.image_url);
              });
              return { ...prev, next: null };
            } else {
              return prev;
            }
          });

          clearInterval(interval);
          activePollingJobRef.current = null;
        } else if (data.status === 'FAILED') {
          clearInterval(interval);
          activePollingJobRef.current = null;
        }
      } catch (err) {
        clearInterval(interval);
        activePollingJobRef.current = null;
      }
    }, 5000);
  }, [getAuthToken, apiUrl, sessionId]);

  const determineResponseType = (promptNum: number, totalChapters: number) => {
    const currentChapter = Math.ceil(promptNum / 7);
    const promptInChapter = promptNum % 7 === 0 ? 7 : promptNum % 7;
    const isLastChapter = currentChapter === Number(totalChapters);

    if (isLastChapter && promptInChapter === 7) return 'final_prompt';
    if (isLastChapter) return 'last_chapter';
    if (promptInChapter === 7) return 'cliffhanger';
    return 'questions';
  };

  const inputRef = useRef<TextInput>(null); // Create a ref for the TextInput

  const verifyImageAvailability = async (url: string): Promise<boolean> => {
    for (let i = 0; i < 5; i++) {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        if (res.ok) return true;
      } catch (err) {
        console.warn(`Retry ${i + 1}: Image not available yet`);
      }
      await new Promise((res) => setTimeout(res, 1000));
    }
    return false;
  };


  const handleTextInputSubmit = async () => {
    stopAudio();

    const authToken = await getAuthToken();
    if (!authToken) {
      Alert.alert('Error', 'Authentication token not found');
      return;
    }
    setLoading(true);

    try {
      // Calculate current chapter and prompt within chapter
      const nextResponseType = determineResponseType(promptNumber, storyData.chapters);

      // API call
      const response = await fetch(`${apiUrl}story/model-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          is_first_message: promptNumber === 0,
          session_id: sessionId,
          text_data: textInput,
          prompt_number: Math.max(promptNumber, 2),
          inputType: 'text',
          voice: voice,
          images: withImages,
          response_type: nextResponseType,
          story_id: storyData.story_id
        }),
      });

      const data = await response.json();
      if (response.ok) {
        const newCurrent = imageJobs.next || data.job_id || null;
        const newNext = data.next_job_id || null;
        setImageJobs({ current: newCurrent, next: newNext });

        if (nextImageUrl && newCurrent === imageJobs.next) {
          const isAvailable = await verifyImageAvailability(nextImageUrl);
          if (isAvailable) {
            setImageUrl(nextImageUrl);
            setNextImageUrl(null);
          } else {
            console.warn('Prefetched image not ready in time');
            setImageUrl(null);
          }
        } else {
          setImageUrl(null);
        }


        if (newNext) pollForNextImage(newNext);
        setHideImage(false);
        setHideText(false);
        
        const nextPromptNumber = promptNumber + 1;
        const isEndOfFirstChapter = nextPromptNumber === 8; //8 is correct

        if (!feedbackShown && isEndOfFirstChapter && Platform.OS !== 'web') {
          setFeedbackVisible(true);
          setFeedbackShown(true);
        }
        setResponseType(determineResponseType(promptNumber, storyData.chapters));
        setPromptNumber(Math.max(nextPromptNumber, 3));
        console.log('prompt number: ' + promptNumber)
        setChapter(getCurrentChapter(nextPromptNumber))

        setStoryContent(data.response || 'No response received.');
        setTextInput('');
        setPages(data.pages);


        setContext({
          sessionId: sessionId,
          selectedStory: {
            ...storyData,
            prompt_number: promptNumber + 1,
          },
          withImages,
        });

        inputRef.current?.blur();

        if (data.audio) {
          setAudioBase64(data.audio);
          saveBase64AudioToFile(data.audio);
        }

        setNextImageUrl(null);
      } else {
        Alert.alert('Error', data.message || 'Failed to update story');
      }
    } catch (error) {
      console.error('Error submitting text input:', error);
      Alert.alert('Error', 'Failed to communicate with server');
    } finally {
      setLoading(false);
    }
  };


  const handleBack = () => {
    // Clear the context when navigating back
    setContext(null);
    navigation.navigate('StoriesPage'); // Navigate to StoriesPage explicitly
  };
  
  const handleMute = async () => {
    if (Platform.OS === 'web') {
      try {
        if (webAudioInstance) {
          let newVolume;
          if (isMuted){
            newVolume = volume > 0 ? volume / 100 : 1/2;
          } else {
            newVolume = 0;
          };
          updateSettings(newVolume * 100);
          webAudioInstance.volume = newVolume;
          console.log('New volume: ', newVolume)
          console.log('webInstance volume: ', webAudioInstance.volume)
          setIsMuted(!isMuted);
        } else {
          console.warn('No active web audio instance found.');
        }
      } catch (error) {
        console.error('Error toggling mute on web:', error);
      }
    } else {
      try {
        if (audioRef.current) {
          let newVolume;
          if (isMuted){
            newVolume = volume > 0 ? volume / 100 : 1/2;
          } else {
            newVolume = 0;
          };
          updateSettings(newVolume * 100);
          await audioRef.current.setVolumeAsync(newVolume);
          setIsMuted(!isMuted);
        } else {
          console.warn('No audio instance to mute/unmute.');
        }
      } catch (error) {
        console.error('Error toggling mute:', error);
      }
    }
  };  

  const getUserID = async (): Promise<string | null> => {
      try {
        const session = await fetchAuthSession();
        const user_id = session?.tokens?.accessToken?.payload?.sub;
        return user_id || null;
      } catch (error) {
        console.error('Error retrieving user ID:', error);
        return null;
      }
    };
  
  const updateSettings = async (volume: number) => {
    try {
      const user_id = await getUserID();
      const authToken = await getAuthToken();
      const response = await fetch(`${apiUrl}settings/set-user-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_id,
          volume,
        }),
      });
      console.log('Settings updated successfully:', await response.json());
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const isChromeBrowser = () => {
    if (Platform.OS !== 'web') return false;
  
    const ua = navigator.userAgent;
    const isBrave = (navigator as any).brave?.isBrave?.() ?? ua.includes('Brave');
  
    return /Chrome/.test(ua) && !/Edg|OPR|Brave/.test(ua) && !isBrave;
  };

  const requestPermissions = async () => {
    try {
      // Request permissions for microphone access
      const { granted } = await Audio.requestPermissionsAsync();
      if (!granted) {
        Alert.alert('Permission Denied', 'Microphone access is required to record audio.');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error requesting microphone permission:', error);
      Alert.alert('Error', 'Failed to request microphone permissions.');
      return false;
    }
  };

  const startListening = async (setter: (value: string) => void) => {
  // 🧹 Clean up any previous listeners/states before starting
  await Voice.stop();
  await Voice.destroy();
  Voice.removeAllListeners();

  // Attach listeners for the duration of this input session only
  Voice.onSpeechResults = (event) => {
    if (event.value && event.value.length > 0) {
      setter(event.value[0]);
    }
    setActiveRecognizer(false);
    Voice.stop();
  };
  Voice.onSpeechEnd = () => {
    setActiveRecognizer(false);
    Voice.stop();
  };
  Voice.onSpeechError = (error) => {
    if (error?.error?.code === '11') {
      // Soft error: "Didn't understand", ignore.
      return;
    }
    console.error('Speech recognition error:', error);
    Alert.alert('Error', 'Please try speaking more clearly.');
    setActiveRecognizer(false);
  };

  if (Platform.OS === 'web') {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      Alert.alert('Error', 'Your browser does not support speech recognition.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event: any) => {
      setter(event.results[0][0].transcript);
    };
    recognition.onerror = (event: any) => {
      console.error('Web Speech error:', event.error);
    };

    recognition.start();
    return;
  }

  // Request permissions for native
  const granted = await requestPermissions();
  if (!granted) return;

  if (Platform.OS === 'android' || Platform.OS === 'ios') {
    try {
      await stopAudio(); // Always stop audio to avoid echo
      setActiveRecognizer(true);
      await Voice.start('en-US');
      // Safety timeout: Stop after 5 seconds to avoid hang
      setTimeout(() => {
        Voice.stop();
        setActiveRecognizer(false);
      }, 5000);
    } catch (err) {
      setActiveRecognizer(false);
      Alert.alert('Error', 'Failed to start speech recognition.');
    }
  }
};

const getCurrentChapter = (promptNumber: number): number => {
  return Math.max(1, Math.ceil((promptNumber - 1) / 7));
}; 

const handleFlag = () => {
  setFlagModalVisible(true);
};

const sendFlag = async ({
  message,
  flagType = 'flag',
  flagSubType, // 'image' | 'text' | 'both'
  imageUrl,
  storyText,
}: {
  message: string;
  flagType?: 'flag' | 'bug' | 'feedback';
  flagSubType?: 'image' | 'text' | 'both';
  imageUrl?: string;
  storyText?: string;
}) => {
  try {
    const payload: Record<string, any> = {
      type: flagType,
      message,
    };

    if (flagType === 'flag') {
      payload.flag_type = flagSubType;
      if (flagSubType === 'image' || flagSubType === 'both') {
        payload.image_url = imageUrl;
      }
      if (flagSubType === 'text' || flagSubType === 'both') {
        payload.story_text = storyText;
      }
    }

    const response = await fetch(`${apiUrl}user/contact-us`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Error submitting flag:', result.error);
      Alert.alert('Error', 'Something went wrong. Please try again.');
      return;
    }

    Alert.alert('Thank you', 'Your report has been submitted.');
  } catch (err) {
    console.error('Flag send failed:', err);
    Alert.alert('Error', 'An error occurred. Please try again later.');
  }
};

const submitFlag = async () => {
  if (
    (imagesEnabled && (!flagType || !flagReason.trim())) ||
    (!imagesEnabled && !flagReason.trim())
  ) {
    Alert.alert(
      imagesEnabled
        ? 'Please select an issue type and provide a reason.'
        : 'Please provide a reason.'
    );
    return;
  }

  const message = `SESSION: ${sessionId}\nPROMPT: ${promptNumber}\n\nREASON:\n${flagReason}`;

  // If images are not enabled, always set flagType to 'text'
  const finalFlagType = imagesEnabled ? flagType : 'text';

  await sendFlag({
    message,
    flagType: 'flag',
    flagSubType: finalFlagType ?? undefined,
    imageUrl,
    storyText: storyContent,
  });

  if (finalFlagType === 'image') setHideImage(true);
  if (finalFlagType === 'text') setHideText(true);
  if (finalFlagType === 'both') {
    setHideImage(true);
    setHideText(true);
  }

  setFlagModalVisible(false);
  setFlagType(null);
  setFlagReason('');
};

const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();


  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaView style={styles.safeArea}>
      {loading && (
        <View style={styles.loadingOverlay}>
          <Image source={require('./assets/wizard.gif')} style={styles.loadingGif} />
        </View>
      )}
      {Platform.OS === 'web' ? (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9,
            height: 60,
            backgroundColor: '#F7F8FB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 16px',
          }}
        >
          <button onClick={handleBack} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <img src={backBtn} style={{ width: 24, height: 24 }} />
          </button>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, paddingRight: 10, }}>
            <button onClick={handleFlag} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <img src={flagBtn} style={{ width: 24, height: 24 }} />
            </button>
            <button onClick={handleMute} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <img
                src={isMuted ? unmuteBtn : muteBtn}
                style={{ width: 24, height: 24 }}
              />
            </button>
          </div>
        </div>
      ) : (
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
          </TouchableOpacity>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <TouchableOpacity onPress={handleFlag}>
              <Image source={require('./assets/flag-icon.png')} style={styles.flagIcon} />
            </TouchableOpacity>
            <TouchableOpacity onPress={handleMute} style={styles.muteButton}>
              <Image
                source={
                  isMuted
                    ? require('./assets/unmute-btn.png')
                    : require('./assets/mute-btn.png')
                }
                style={styles.muteIcon}
              />
            </TouchableOpacity>
          </View>
        </View>
      )}
      <KeyboardAvoidingView
        style={{ flex: 1, backgroundColor: '#F7F8FB' }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 60 : 0}
      >
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          Platform.OS === 'web' ? { paddingVertical: 120 } : null
        ]}
      >
        <View style={styles.container}>
          <View style={styles.chapterContainer}>
            <Text style={styles.chapterText}>Chapter {chapter}</Text>
          </View>
          {imagesEnabled && !hideImage && (
            imageUrl ? (
              <Image source={{ uri: imageUrl }} style={styles.image} />
            ) : imageJobs.current ? (
              <View style={styles.placeholder}>
                <ActivityIndicator size="large" color="#ccc" />
              </View>
            ) : (promptNumber === 1 && (initialResponse?.first_image || storyData.first_image)) ? (
              <Image source={{ uri: initialResponse?.first_image || storyData.first_image }} style={styles.image} />
            ) : null
          )}
          {!hideText && (
            <ScrollView
              ref={scrollViewRef}
              style={[
                styles.scrollView, 
                { maxHeight: imagesEnabled ? 200 : 500 }
              ]}
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
            >
              <Text style={styles.text}>{storyContent}</Text>
            </ScrollView>
          )}
          {responseType !== 'cliffhanger' &&
          responseType !== 'final_prompt' &&
          promptNumber >= Math.max(...pages.map(p => p.prompt_number)) && (
            <View style={styles.inputGroup}>
              <TextInput
                ref={inputRef} // Attach the ref
                style={styles.input}
                placeholder="What do you want to do?"
                placeholderTextColor="#6C6C6C"
                value={textInput}
                onChangeText={setTextInput}
              />
              {(Platform.OS === 'ios') || (Platform.OS === 'web' && isChromeBrowser()) && (
                <TouchableOpacity style={styles.micButton} onPress={() => startListening(setTextInput)}>
                  <Text>🎤</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
          <View style={styles.buttonContainerRow}>
          {showBackButton && (
            <View style={styles.shadowButtonWrapper}>
              <View style={styles.backCircleShadow} />
              <TouchableOpacity onPress={handlePrevious} style={styles.backCircleButton}>
              <Image
                source={require('./assets/back-btn.png')}
                style={{ width: 24, height: 24, tintColor: 'white' }}
              />
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.shadowButtonWrapper}>
            <View style={responseType === 'cliffhanger' ? styles.continueShadowBackground : styles.nextShadowBackground} />
            <TouchableOpacity
              disabled={isNextDisabled && !showNextPageButton}
              onPress={() => {
                if (responseType === 'final_prompt') {
                  const isMobile = Platform.OS === 'ios' || Platform.OS === 'android';

                  if (!feedbackShown && isMobile) {
                    setFeedbackVisible(true);
                    setFeedbackShown(true);
                  } else {
                    navigation.navigate('StoriesPage');
                  }
                } else {
                  handleNext();
                }
              }}
              style={[
                styles.nextButton,
                (isNextDisabled && !showNextPageButton) && { opacity: 0.5 },
              ]}
            >
              <Text style={styles.buttonText}>
                {
                    showNextPageButton
                    ? 'Next'
                    : responseType === 'cliffhanger'
                    ? 'Continue'
                    : responseType === 'final_prompt'
                    ? 'The End'
                    : 'Submit'
                }
              </Text>
            </TouchableOpacity>
          </View>
        </View>
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
      </SafeAreaView>
      {flagModalVisible && (
        <View style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', zIndex: 20
        }}>
          <View style={{
            backgroundColor: 'white', padding: 20, borderRadius: 10, width: '90%', maxWidth: 400
          }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 10 }}>What seems to be the issue?</Text>

            {imagesEnabled ? (
              ['image', 'text', 'both'].map((option) => (
                <TouchableOpacity
                  key={option}
                  onPress={() => setFlagType(option as 'image' | 'text' | 'both')}
                  style={{
                    backgroundColor: flagType === option ? '#544564' : '#ccc',
                    padding: 10, borderRadius: 8, marginVertical: 5
                  }}
                >
                  <Text style={{ color: '#fff', textAlign: 'center' }}>{capitalize(option)}</Text>
                </TouchableOpacity>
              ))
            ) : null}

            <TextInput
              multiline
              numberOfLines={4}
              placeholder="Tell us why..."
              value={flagReason}
              onChangeText={setFlagReason}
              style={{
                marginTop: 15, borderColor: '#ccc', borderWidth: 1,
                borderRadius: 8, padding: 10, textAlignVertical: 'top'
              }}
            />

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15 }}>
              <TouchableOpacity
                onPress={() => {
                  setFlagModalVisible(false);
                  setFlagType(null);
                  setFlagReason('');
                }}
              >
                <Text style={styles.flagModalButton}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={submitFlag}>
                <Text style={styles.flagModalButton}>Submit</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
      <FeedbackModal
        visible={feedbackVisible}
        onClose={() => {
          setFeedbackVisible(false);
          if (responseType === 'final_prompt') {
            navigation.navigate('StoriesPage');
          }
        }}
        sessionId={sessionId}
      />
    </GestureHandlerRootView>
  );  
};

const styles = StyleSheet.create({
  container: {
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F7F8FB',
  },
  scrollContent: {
    flexGrow: 1,
    backgroundColor: '#F7F8FB',
    paddingTop: 60,       // Header height
    paddingBottom: 100,   // Enough space for bottom button
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject, // Fills the entire screen
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent gray
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 11, // Ensure it overlays other content
  },
  loadingGif: {
    width: 150,
    height: 150,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    height: 60,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F7F8FB',
    paddingHorizontal: 16,
  },
  safeArea: {
    flex: 1,
    backgroundColor: '#F7F8FB',
  },
  backButton: {
    marginRight: 10
  },
  backIcon: {
    width: 24,
    height: 24
  },
  backIconBtn: {
    width: 24,
    height: 24,
    color: 'white'
  },
  muteButton: {
    marginRight: 10,
  },
  muteIcon: {
    width: 24,
    height: 24,
  },
  flagIcon: {
    width: 24,
    height: 24,
  },
  scrollView: {
    width: '100%',
    minHeight: 175,
    marginBottom: '3%',
  },
  chapterContainer: {
    width: '100%', // Full width
    marginBottom: 10, // Space below chapter text
    maxWidth: 500,
  },
  chapterText: {
    fontSize: 20, // Adjust as needed
    fontWeight: 'bold',
    color: '#333',
    fontFamily: 'Futura',
    textAlign: 'left', // Align text to the left
  },
  text: {
    fontSize: 18,
    color: '#333',
    padding: 10,
    fontFamily: 'Futura',
    maxWidth: 500,
    alignSelf: 'center',
  },
  image: {
    width: '100%',
    maxWidth: 500,
    aspectRatio: 1,
    borderRadius: 15,
    marginBottom: 20,
    alignSelf: 'center',  // Keep image centered
  },
  placeholder: {
    width: '100%',
    maxWidth: 500,
    aspectRatio: 1,
    borderRadius: 15,
    marginBottom: 20,
    alignSelf: 'center', // Matches the image's alignment
    justifyContent: 'center', // Centers the spinner vertically
    alignItems: 'center', // Centers the spinner horizontally
    backgroundColor: '#f0f0f0', // Neutral background for the placeholder
  },
  inputGroup: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    backgroundColor: '#D9D9D9', 
    borderRadius: 20,
    paddingHorizontal: 20,
    width: '90%',
    maxWidth: 500,
  },
  input: { 
    paddingVertical: 20, 
    backgroundColor: '#D9D9D9', 
    fontFamily: 'Futura',
    fontSize: 18,
    textAlign: 'center',
    borderRadius: 20,
    marginHorizontal: 10,
    width: '80%',
    maxWidth: 500,
    height: 80,
    borderWidth: 0,
    ...Platform.select({
      web: {
        outlineStyle: 'none', // Remove the outline for web
      },
    }),
  },
  micButton: { 
    marginLeft: 10, 
    backgroundColor: '#AFC9BF',
    padding: 10,
    borderRadius: 50,
  },
  buttonContainerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'center',
    marginTop: 10,
    gap: 10,
  },
  backCircleButton: {
    backgroundColor: '#544564',
    width: 60,
    height: 60,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 20,
    zIndex: 1,
  },
  
  backCircleShadow: {
    position: 'absolute',
    width: 60,
    height: 60,
    backgroundColor: '#AFC9BF',
    borderRadius: 10,
    top: 5,
    left: 2,
    zIndex: 0,
  },
  buttonContainer: {
    alignSelf: 'center',
  },
  nextButton: {
    backgroundColor: '#544564', 
    padding: 15, 
    borderRadius: 15, 
    alignItems: 'center', 
    alignSelf: 'center',
    justifyContent: 'center',
    width: 238,
    height: 60,
    marginTop: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    marginBottom: '3%',
  },
  shadowButtonWrapper: {
    position: 'relative',
  },  
  nextShadowBackground: {
    position: 'absolute',
    width: 238,
    height: 60,
    backgroundColor: '#AFC9BF',
    borderRadius: 16,
    top: '20%',
    left: '1%',
  },
  continueShadowBackground: {
    position: 'absolute',
    width: 238,
    height: 60,
    backgroundColor: '#AFC9BF',
    borderRadius: 16,
    top: '20%',
    left: '1%',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
  },
  flagModalButton: {
    color: 'white', 
    backgroundColor: '#534466', 
    padding: 10,
    borderRadius: 20,
  }
});

export default StoryPage;